import {
  FC,
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { SessionKey } from 'src/lib/session-storage';
import {
  CLEAR_ALL_RECENT_BOOKINGS,
  CLEAR_RECENT_BOOKING,
  RECORD_RECENT_BOOKING,
  RecentlyBookedAppointments,
  SET_SSB_INFO,
  SSBActionTypes,
  SSBContextValue,
  SSBState,
} from './types';

const SSBContext = createContext<SSBContextValue | undefined>(undefined);

const RECENT_BOOKING_TTL_MS = 5 * 60 * 1000;

const initialState: SSBState = {
  flowState: null,
  recentlyBookedAppointments: {},
};

/**
 * Recent bookings are persisted to sessionStorage so that a page reload (or re-navigation) within
 * the TTL still recovers appointments the funnel just booked but the backend has not yet reflected
 * (Acuity webhook lag). Without this, a reload wipes the in-memory map and the just-booked step is
 * offered again — the duplicate-booking bug. sessionStorage is per-tab and cleared on tab close,
 * which matches the short-lived nature of a booking session.
 */
const readPersistedRecentBookings = (): RecentlyBookedAppointments => {
  try {
    const raw = sessionStorage.getItem(SessionKey.RecentlyBookedAppointments);
    return raw ? pruneStale(JSON.parse(raw) as RecentlyBookedAppointments) : {};
  } catch {
    return {};
  }
};

const persistRecentBookings = (bookings: RecentlyBookedAppointments): void => {
  try {
    sessionStorage.setItem(SessionKey.RecentlyBookedAppointments, JSON.stringify(bookings));
  } catch {
    // Best-effort: ignore storage failures (private mode, quota, unavailable sessionStorage).
  }
};

const pruneStale = (
  bookings: RecentlyBookedAppointments,
  now = Date.now()
): RecentlyBookedAppointments => {
  let changed = false;
  const next: RecentlyBookedAppointments = {};
  for (const [code, entry] of Object.entries(bookings)) {
    if (entry && now - entry.recordedAt < RECENT_BOOKING_TTL_MS) {
      next[code as keyof RecentlyBookedAppointments] = entry;
    } else if (entry) {
      changed = true;
    }
  }
  return changed ? next : bookings;
};

const ssbReducer = (prevState: SSBState, action: SSBActionTypes): SSBState => {
  switch (action.type) {
    case SET_SSB_INFO:
      return { ...prevState, flowState: action.payload };

    case RECORD_RECENT_BOOKING: {
      const pruned = pruneStale(prevState.recentlyBookedAppointments);
      return {
        ...prevState,
        recentlyBookedAppointments: {
          ...pruned,
          [action.payload.code]: {
            appointment: action.payload.appointment,
            recordedAt: Date.now(),
          },
        },
      };
    }

    case CLEAR_RECENT_BOOKING: {
      const { [action.payload.code]: _removed, ...rest } = prevState.recentlyBookedAppointments;
      return { ...prevState, recentlyBookedAppointments: rest };
    }

    case CLEAR_ALL_RECENT_BOOKINGS:
      return { ...prevState, recentlyBookedAppointments: {} };

    default:
      return prevState;
  }
};

export const SSBProvider: FC<{ children: ReactNode }> = ({ children }): ReactElement => {
  const [state, dispatchSSB] = useReducer(ssbReducer, initialState, (base) => ({
    ...base,
    recentlyBookedAppointments: readPersistedRecentBookings(),
  }));

  const recentlyBookedAppointments = useMemo(
    () => pruneStale(state.recentlyBookedAppointments),
    [state.recentlyBookedAppointments]
  );

  // Persist the recent-bookings map so a reload within the TTL recovers just-booked appointments.
  useEffect(() => {
    persistRecentBookings(state.recentlyBookedAppointments);
  }, [state.recentlyBookedAppointments]);

  return (
    <SSBContext.Provider
      value={{
        ssbState: state.flowState,
        recentlyBookedAppointments,
        dispatchSSB,
      }}
    >
      {children}
    </SSBContext.Provider>
  );
};

export const useSSB = (): SSBContextValue => {
  const ssbContext = useContext(SSBContext);

  if (!ssbContext) {
    throw new Error('useSSB must be used within a SSBContext.Provider');
  }

  return ssbContext;
};
